/**
 * Equipment library — the real 16, from `GET /v1/equipment`.
 *
 * NOT the design's illustrative 13. The design's embedded library rounds the
 * typicals, invents peak at roughly 1.2x max, and re-labels the categories;
 * every real row has `peak = max` because no inrush data exists anywhere.
 * Database categories are authoritative here, unmapped. A presentation-only
 * grouping is a separate proposal, not something to do quietly in a renderer.
 *
 * Provenance sits beside the value: `kind`, `confidence` and source are on
 * the row, not behind a disclosure, because these figures travel as
 * screenshots.
 */

import { useEffect, useMemo, useState } from 'react';
import { modeller, type EquipmentClass } from '../api';
import { PageHeader } from '../shell/Shell';
import { ErrorState, Loading } from '../shell/states';
import { Note, Tag } from '../shell/provenance';
import './equipment.css';

type State =
  | { t: 'loading' } | { t: 'error'; e: unknown } | { t: 'ok'; rows: EquipmentClass[] };

function num(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function Row({ r }: { r: EquipmentClass }) {
  /* Every current row has peak = max, because no entry carries inrush data.
     Saying so on the row is the honest version of a column that looks
     redundant. */
  const peakEqualsMax = r.peak_kw === r.max_kw;
  return (
    <tr>
      <th scope="row">
        <span className="pem-eq__name">{r.name}</span>
        <span className="pem-eq__key">{r.key}</span>
      </th>
      <td className="pem-eq__cat">{r.category}</td>
      <td className="pem-num">{num(r.min_kw)}</td>
      <td className="pem-num pem-num--strong">{num(r.typical_kw)}</td>
      <td className="pem-num">{num(r.max_kw)}</td>
      <td className="pem-num">
        {num(r.peak_kw)}
        {peakEqualsMax && <span className="pem-eq__flat" title="No inrush data: peak equals max">=</span>}
      </td>
      <td className="pem-num">{Math.round(r.duty_cycle * 100)}%</td>
      <td className="pem-num">{num(r.typical_duration_h)}</td>
      <td className="pem-eq__prov">
        <Tag tone={r.kind === 'assumed' ? 'assumed' : 'confirmed'}>{r.kind}</Tag>
        <span className="pem-provenance">
          Confidence {r.confidence} · Source {r.source_key ?? 'NONE'}
          {r.source_version ? ` ${r.source_version}` : ''}
        </span>
      </td>
    </tr>
  );
}

export function EquipmentLibrary() {
  const [state, setState] = useState<State>({ t: 'loading' });

  useEffect(() => {
    let live = true;
    modeller.equipment()
      .then((rows) => { if (live) setState({ t: 'ok', rows }); })
      .catch((e) => { if (live) setState({ t: 'error', e }); });
    return () => { live = false; };
  }, []);

  const summary = useMemo(() => {
    if (state.t !== 'ok') return null;
    const assumed = state.rows.filter((r) => r.kind === 'assumed').length;
    const flat = state.rows.filter((r) => r.peak_kw === r.max_kw).length;
    const noPf = state.rows.filter((r) => r.power_factor == null).length;
    return { assumed, flat, noPf, total: state.rows.length };
  }, [state]);

  if (state.t === 'loading') return <><PageHeader title="Equipment library" /><Loading what="the library" /></>;
  if (state.t === 'error') return <><PageHeader title="Equipment library" /><ErrorState what="the library" error={state.e} /></>;

  const rows = [...state.rows].sort(
    (a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));

  return (
    <>
      <PageHeader title="Equipment library" count={`${rows.length} classes`} />

      <div className="pem-eq__head">
        <Note>
          {summary && (
            <>
              {summary.assumed} of {summary.total} entries are <strong>kind=assumed</strong>.{' '}
              {summary.flat} have peak equal to max — no inrush data exists, so the
              three peak bounds cannot be separated.{' '}
              {summary.noPf} carry no power factor, which is why kVA is refused.
            </>
          )}
        </Note>
      </div>

      <div className="pem-eq__scroll">
        <table className="pem-eq">
          <thead>
            <tr>
              <th scope="col">Class</th>
              <th scope="col">Category</th>
              <th scope="col" className="pem-num">Min</th>
              <th scope="col" className="pem-num">Typical</th>
              <th scope="col" className="pem-num">Max</th>
              <th scope="col" className="pem-num">Peak</th>
              <th scope="col" className="pem-num">Duty</th>
              <th scope="col" className="pem-num">Hours</th>
              <th scope="col">Provenance</th>
            </tr>
            <tr className="pem-eq__units">
              <td colSpan={2} />
              <td className="pem-num" colSpan={4}>kW</td>
              <td className="pem-num" />
              <td className="pem-num">h</td>
              <td />
            </tr>
          </thead>
          <tbody>{rows.map((r) => <Row key={r.key} r={r} />)}</tbody>
        </table>
      </div>

      <div className="pem-detail-foot">
        <Note>
          Quantity of 1 means one departmental package load, not one fixture.
          Categories are the database's own; no presentation grouping is applied.
        </Note>
      </div>
    </>
  );
}
